import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import {
    ActivatedRoute,
    Params,
} from '@angular/router';
import {
    enumMapper,
    IJob,
    ILog,
    IRobot,
    LogLevel,
} from '@models';
import {
    FILTER_BUILDER,
    ODataFilterBuilder,
} from '@pages/generics/filter-builder';
import {
    GRID_SERVICE,
    GridPageState,
    ODataGridComponent,
    STATE_HANDLER,
    urlStateHandler,
} from '@pages/generics/grid';
import {
    buildODataQuery,
    FileDownloadService,
    JobService,
    LogService,
    MachineService,
    MapperDefinition,
    ModalService,
    ODataComparisonProperty,
    ODataQueryOptions,
    ProcessService,
    RobotService,
    SessionsService,
} from '@services';
import {
    IFilterModel,
    ISortModel,
} from '@uipath/angular/components/ui-grid/models';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    combineLatest,
    Observable,
    of,
} from 'rxjs';
import {
    filter,
    map,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs/operators';

import { LogDetailsModalComponent } from './modals';

enum LogEntityType {
    None,
    Robot,
    Job,
}

export class LogsFilterBuilder extends ODataFilterBuilder<ILog> {
    public build(
        page: PageEvent,
        sort: ISortModel<ILog>,
        searchFilters: IFilterModel<ILog>[],
        originalFilters: IFilterModel<ILog>[],
        expand: string[],
        select: string[],
        mapper: MapperDefinition<ILog>,
    ): ODataQueryOptions {
        const filters = cloneDeep(originalFilters);

        const userFilterIndex = filters.findIndex(f => f.property === 'user');
        if (userFilterIndex !== -1) {
            const [userFilter] = filters.splice(userFilterIndex, 1);

            const queryOptions = super.build(page, sort, searchFilters, filters, expand, select, mapper);
            (queryOptions.filter! as ODataComparisonProperty).and!.push({
                or: (userFilter.value as string[]).map(RobotName => ({
                    RobotName,
                })),
            });
            return queryOptions;
        }

        return super.build(page, sort, searchFilters, filters, expand, select, mapper);
    }
}

@Component({
    templateUrl: 'logs.page.html',
    providers: [
        JobService,
        SessionsService,
        MachineService, {
            provide: GRID_SERVICE,
            useClass: LogService,
        },
        {
            provide: FILTER_BUILDER,
            useClass: LogsFilterBuilder,
        },
        {
            provide: STATE_HANDLER,
            useValue: urlStateHandler,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogsPageComponent extends ODataGridComponent<ILog, number> implements OnInit {
    public realCount$!: Observable<number>;

    public logLevels = enumMapper(LogLevel, {
        resourceKey: 'LogLevel',
    });

    public defaultLogLevel = this.logLevels
        .find(l => l.value === LogLevel.Trace.toString());
    public LogEntityType = LogEntityType;
    public entityType!: LogEntityType;

    public isMachinesViewGranted$ = this._appState.user.permissions.isGranted('Machines.View');
    public isProcessesViewGranted$ = this._appState.user.permissions.isGranted('Processes.View');
    public isRobotsViewGranted$ = this._appState.user.permissions.isGranted('Robots.View');

    private _exportedCsvFileNameSubject = '';

    private _reportsUrl$!: Observable<string>;

    private _route = this._injector.get<ActivatedRoute>(ActivatedRoute);
    private _logService = this._service as LogService;
    private _robotService = this._injector.get<RobotService>(RobotService);
    private _jobService = this._injector.get<JobService>(JobService);
    private _modalService = this._injector.get<ModalService>(ModalService);
    private _downloadService = this._injector.get<FileDownloadService>(FileDownloadService);

    ngOnInit() {
        this._reportsUrl$ = this._activeQuery$.pipe(
            map(
                query => `${this._logService.reportsUrl}${buildODataQuery(query)}&` +
                    `fileNameSubject=${this._exportedCsvFileNameSubject}`,
            ),
        );

        const entity$ = this._route.paramMap.pipe(
            tap(() => this._state$.next(GridPageState.pending)),
            switchMap(params => {
                const entity = params.get('entity');
                const id = params.get('id');

                switch (entity) {
                    case 'robots':
                        this.entityType = LogEntityType.Robot;
                        return this._robotService.getInstance(Number(id));
                    case 'jobs':
                        this.entityType = LogEntityType.Job;
                        return this._jobService.getInstance(Number(id), ['Release']);
                    default:
                        this.entityType = LogEntityType.None;
                        return of(null);
                }
            }),
        );

        combineLatest([
            entity$,
            this._route.paramMap,
        ]).pipe(
            tap(this._configureLogs),
            tap(() => this._state$.next(GridPageState.ready)),
            takeUntil(this._destroyed$),
        ).subscribe();

        this.realCount$ = this._activeQuery$.pipe(
            filter(() => this._state$.value === GridPageState.ready),
            switchMap(qo => this._logService.getRealCount(qo)),
        );
    }

    public showDetails(entry: ILog) {
        this._modalService.openModal(LogDetailsModalComponent, entry);
    }

    public downloadReport(event: MouseEvent) {
        event.preventDefault();
        this._reportsUrl$.pipe(
            take(1),
        ).subscribe((url) => {
            this._downloadService.download(url);
        });
    }

    public machineNameSuggest = (term: string, size: number, skip: number) => {
        return this._injector.get<MachineService>(MachineService).machineNameSuggest(term, size, skip);
    }

    public processNameSuggest = (term: string, size: number, skip: number) => {
        return this._injector.get<ProcessService>(ProcessService).suggestName(term, size, skip);

    }
    public usernameSuggest = (term: string, size: number, skip: number) => {
        return this._injector.get<SessionsService>(SessionsService).robotsByUsernameSuggest(term, size, skip);
    }

    protected _ouDependentFilters() {
        return ['processName'];
    }

    private _configureLogs = ([entity, params]: [IJob | IRobot | null, Params]) => {
        const entityType = params.get('entity');

        switch (entityType) {
            case 'robots':
                entity = (entity as IRobot);

                this._filters$.next([{
                    value: entity.name,
                    method: 'eq',
                    property: 'robotName',
                }]);

                this._exportedCsvFileNameSubject = `${this._translate.instant('export_logs_robot')}_${entity.name}`;
                break;
            case 'jobs':
                entity = (entity as IJob);

                this._filters$.next([{
                    value: entity.key,
                    type: 'guid',
                    method: 'eq',
                    property: 'jobKey',
                }]);

                this._exportedCsvFileNameSubject = `${this._translate.instant('export_logs_job')}_${entity.release.name}`;
                break;
            default:
                this._exportedCsvFileNameSubject = `${this._translate.instant('export_logs_filename')}`;
        }
    }
}
